/**
 * Authentication Service Interface
 * 
 * This service provides an abstraction layer for authentication operations.
 * The implementation can be easily swapped (Cognito, Auth0, custom backend, etc.)
 * without changing the consuming code.
 */

/**
 * Authentication Service Interface
 * All auth service implementations must conform to this interface
 */
export class AuthServiceInterface {
  /**
   * Initialize the auth service with configuration
   * @param {Object} config - Configuration object
   */
  async initialize(config) {
    throw new Error('initialize() must be implemented');
  }

  /**
   * Sign up a new user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<{success: boolean, result?: any, error?: string}>}
   */
  async signUp(email, password) {
    throw new Error('signUp() must be implemented');
  }

  /**
   * Confirm user sign up with verification code
   * @param {string} email - User's email
   * @param {string} code - Verification code
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async confirmSignUp(email, code) {
    throw new Error('confirmSignUp() must be implemented');
  }

  /**
   * Sign in an existing user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<{success: boolean, result?: any, error?: string}>}
   */
  async signIn(email, password) {
    throw new Error('signIn() must be implemented');
  }

  /**
   * Sign out the current user
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async signOut() {
    throw new Error('signOut() must be implemented');
  }

  /**
   * Get the currently authenticated user
   * @returns {Promise<{username: string, email: string, accessToken?: string, identityId?: string} | null>}
   */
  async getCurrentUser() {
    throw new Error('getCurrentUser() must be implemented');
  }

  /**
   * Get a current token immediately before an authenticated API request.
   * Providers may refresh an expired session before returning it.
   */
  async getIdToken() {
    throw new Error('getIdToken() must be implemented');
  }

  /**
   * Check current authentication state
   * @returns {Promise<{authenticated: boolean, user?: any, session?: any}>}
   */
  async checkAuthState() {
    throw new Error('checkAuthState() must be implemented');
  }

  /**
   * Initiate password reset flow
   * @param {string} email - User's email
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async resetPassword(email) {
    throw new Error('resetPassword() must be implemented');
  }

  /**
   * Confirm password reset with verification code
   * @param {string} email - User's email
   * @param {string} code - Verification code
   * @param {string} newPassword - New password
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async confirmResetPassword(email, code, newPassword) {
    throw new Error('confirmResetPassword() must be implemented');
  }
}
